import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import type { WorkspaceRole, WorkspaceRoleString } from 'api';
import React, { useEffect, useState } from 'react';

import { FormDialogActions, FormDialogContent, RoleOptionRow } from '../team.styles';

interface RoleOption {
  value: WorkspaceRole;
  label: string;
  description: string;
}

const OWNER_OPTION: RoleOption = {
  value: 0,
  label: 'Owner',
  description: 'Full access, can manage workspace and billing.',
};

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: 1,
    label: 'Admin',
    description: 'Can see and edit everything. Only Owner can remove admin role.',
  },
  {
    value: 2,
    label: 'Project Manager',
    description:
      'Can edit all Projects they manage, and see and approve all time on those Projects.',
  },
];

const ROLE_STRING_TO_NUM: Record<WorkspaceRoleString, WorkspaceRole> = {
  Owner: 0,
  Admin: 1,
  ProjectManager: 2,
  TeamMember: 3,
};

interface EditRoleModalProps {
  open: boolean;
  memberName: string;
  currentRole: WorkspaceRoleString;
  onClose: () => void;
  onSave: (role: WorkspaceRole) => void;
  isPending?: boolean;
  isViewerOwner?: boolean;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  open,
  memberName,
  currentRole,
  onClose,
  onSave,
  isPending = false,
  isViewerOwner = false,
}) => {
  const visibleOptions = isViewerOwner ? [OWNER_OPTION, ...ROLE_OPTIONS] : ROLE_OPTIONS;
  const [selected, setSelected] = useState<WorkspaceRole>(
    () => ROLE_STRING_TO_NUM[currentRole] ?? 3,
  );

  useEffect(() => {
    if (open) setSelected(ROLE_STRING_TO_NUM[currentRole] ?? 3);
  }, [open, currentRole]);

  const handleToggle = (value: WorkspaceRole) => {
    // Clicking the already-selected role deselects it, defaulting back to TeamMember (3)
    setSelected((prev) => (prev === value ? 3 : value));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{memberName}'s role</DialogTitle>

      <FormDialogContent>
        {visibleOptions.map((opt) => (
          <RoleOptionRow key={opt.value}>
            <Checkbox
              checked={selected === opt.value}
              onChange={() => handleToggle(opt.value)}
              color="secondary"
              sx={{ mt: -0.5 }}
            />
            <div>
              <Typography variant="body2" fontWeight={500}>
                {opt.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {opt.description}
              </Typography>
            </div>
          </RoleOptionRow>
        ))}
      </FormDialogContent>

      <FormDialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          disabled={isPending}
          onClick={() => onSave(selected)}
        >
          Save
        </Button>
      </FormDialogActions>
    </Dialog>
  );
};
